import { useAppTranslation } from "@/utils";
import { ConsumerOrderBriefStatus, InstallmentStatus } from "@payping/api/app/api/bnplConsumerTypes";
import { CuToman, FiChevronLeft } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { moment } from "@payping/utils/dateUtils";
import { Button, Card, Divider, Flex, Tag, Typography } from "@payping/web-components";
import { FC } from "react";

interface InstallmentCardProps {
  installmentNumber: number;
  dueDate?: moment.Moment;
  amount?: number;
  penaltyAmount?: number;
  installmentStatus?: InstallmentStatus;
  handlePay?: () => void;
  isPayAble?: boolean;
  orderStatus?: ConsumerOrderBriefStatus;
}
const OrderDetailsInstallmentCard: FC<InstallmentCardProps> = ({
  handlePay,
  installmentNumber,
  amount,
  dueDate,
  penaltyAmount,
  isPayAble,
  installmentStatus,
  orderStatus,
}) => {
  const { t } = useAppTranslation();

  return (
    <Card size="small">
      <Flex gap={"middle"} vertical className="tw:w-full">
        <Flex gap={"small"} justify="space-between" className="tw:w-full" align="center">
          <Typography.Text className="tw:m-0!">
            {`${t("common.installment")} ${(t("common.countingNumbers", { returnObjects: true }) as string[])[(installmentNumber ?? 1) - 1]}`}
          </Typography.Text>

          {orderStatus === ConsumerOrderBriefStatus.SemiCanceled ? (
            <Tag className="tw:m-0!">{t("common.installmentStatus.disabled")}</Tag>
          ) : (
            <>
              {orderStatus === ConsumerOrderBriefStatus.Canceled ? (
                <Tag className="tw:m-0!">{t("common.installmentStatus.canceled")}</Tag>
              ) : (
                <>
                  {installmentStatus === InstallmentStatus.Defaulted ? (
                    <Tag className="tw:m-0!">{t("common.installmentStatus.defaulted")}</Tag>
                  ) : null}

                  {installmentStatus === InstallmentStatus.Delayed ? (
                    <Tag className="tw:m-0!">{t("common.installmentStatus.delayed")}</Tag>
                  ) : null}

                  {installmentStatus === InstallmentStatus.PaidOff ? (
                    <Tag className="tw:m-0!">{t("common.installmentStatus.paidOff")}</Tag>
                  ) : null}

                  {installmentStatus === InstallmentStatus.Waiting ? (
                    <Tag className="tw:m-0!">{t("common.installmentStatus.waiting")}</Tag>
                  ) : null}
                  {installmentStatus === InstallmentStatus.SalaryDeductionWaiting ? (
                    <Tag className="tw:m-0!">{t("common.installmentStatus.salaryDeductionWaiting")}</Tag>
                  ) : null}
                  {installmentStatus === InstallmentStatus.SalaryDeductionPaidOff ? (
                    <Tag className="tw:m-0!">{t("common.installmentStatus.salaryDeductionPaidOff")}</Tag>
                  ) : null}
                </>
              )}
            </>
          )}
        </Flex>
        <Flex gap={"small"} justify="space-between" className="tw:w-full" align="center">
          <Typography.Text type="secondary">
            {dueDate ? `${t("common.due")} ${dueDate.locale("fa").format("D MMMM YYYY")}` : ""}
          </Typography.Text>
          <Flex align="center">
            <Typography.Text className="tw:mr-auto">{`${formatLocalizedNumber({ value: amount ?? 0 })}`}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        </Flex>
        {penaltyAmount ? (
          <Flex align="center">
            <Typography.Text>
              {t("home.installmentCard.penaltyText", {
                amount: `${formatLocalizedNumber({ value: penaltyAmount ?? 0 })}`,
              })}
            </Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        ) : null}

        {isPayAble ? (
          <>
            <Divider className="tw:m-0!" />
            <Flex justify="end" align="center" className="tw:w-full">
              <Button className="tw:px-0!" type="link" onClick={handlePay}>
                <Flex align="center">
                  {t("common.installmentPayment")} <FiChevronLeft width={20} />
                </Flex>
              </Button>
            </Flex>
          </>
        ) : null}
      </Flex>
    </Card>
  );
};

export default OrderDetailsInstallmentCard;
